import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../api/Axios";

const ProviderDocsTable = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [lightboxDocId, setLightboxDocId] = useState(null);
  const [lightboxDocType, setLightboxDocType] = useState('image'); // 'image' or 'pdf'
  const [expiryDate, setExpiryDate] = useState('');
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Backend base URL for media files
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

  // Helper function to determine file type
  const getFileType = (url) => {
    if (!url) return 'document';
    
    const extension = url.split('.').pop().toLowerCase();
    
    // PDF files
    if (['pdf'].includes(extension)) return 'pdf';
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
    
    // Other document types (Word, Excel, PowerPoint, etc.)
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf'].includes(extension)) return 'document';
    
    // Default to document for unknown types
    return 'document';
  };

  useEffect(() => {
    console.log('ProviderDocsTable: Starting API call to /accounts/providers-with-docs/');
    setLoading(true);
    setError(null);
    
    axios.get("/accounts/providers-with-docs/")
      .then(res => {
        console.log('ProviderDocsTable: API call successful', res.data);
        setProviders(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('ProviderDocsTable: API call failed', err);
        setError(err.message || 'Failed to fetch providers');
        setProviders([]);
        setLoading(false);
      });
  }, []);

  // Early return for loading state
  if (loading) {
    return (
      <div className="container my-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading providers...</p>
        </div>
      </div>
    );
  }

  // Early return for error state
  if (error) {
    return (
      <div className="container my-4">
        <div className="alert alert-danger" role="alert">
          <h5>Error Loading Providers</h5>
          <p>Failed to fetch provider data: {error}</p>
          <button 
            className="btn btn-outline-danger btn-sm" 
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Handle document actions
  const handleAccept = async () => {
    if (!expiryDate) {
      alert("Please select an expiry date before accepting.");
      return;
    }
    
    try {
      // Update status to accepted
      await axios.post("/accounts/documents/update-status/", {
        document_id: lightboxDocId,
        status: "Accepted",
        document_type: "provider"
      });
      
      // Update expiry date
      await axios.post("/accounts/documents/update-expiry/", {
        document_id: lightboxDocId,
        expiry_date: expiryDate,
        document_type: "provider"
      });
      
      alert("Provider document accepted successfully!");
      
      // Close lightbox and reset form
      setLightboxImg(null);
      setLightboxDocId(null);
      setLightboxDocType('image');
      setExpiryDate('');
      setComments('');
      
      // Refresh the providers list
      const res = await axios.get("/accounts/providers-with-docs/");
      setProviders(res.data);
    } catch (error) {
      console.error("Error accepting provider document:", error);
      alert("Error accepting provider document. Please try again.");
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      alert("Please enter a comment before rejecting the document.");
      return;
    }
    
    try {
      // Update status to rejected
      await axios.post("/accounts/documents/update-status/", {
        document_id: lightboxDocId,
        status: "Rejected",
        document_type: "provider"
      });
      
      // Update comments
      await axios.post("/accounts/documents/update-comments/", {
        document_id: lightboxDocId,
        comments: comments,
        document_type: "provider"
      });
      
      alert("Provider document rejected successfully!");
      
      // Close lightbox and reset form
      setLightboxImg(null);
      setLightboxDocId(null);
      setLightboxDocType('image');
      setExpiryDate('');
      setComments('');
      
      // Refresh the providers list
      const res = await axios.get("/accounts/providers-with-docs/");
      setProviders(res.data);
    } catch (error) {
      console.error("Error rejecting provider document:", error);
      alert("Error rejecting provider document. Please try again.");
    }
  };

  // Function to handle provider details display
  const handleProviderDetails = (provider) => {
    navigate(`/backoffice-dashboard/ManageProviders/${provider.id}`);
  };



  const getProviderDisplayName = (provider) => {
    if (provider.provider_type === 'entity') {
      return provider.company_name || 'N/A';
    }
    return `${provider.first_name || 'N/A'} ${provider.last_name || ''}`.trim() || 'N/A';
  };

  const getProviderType = (provider) => {
    return provider.provider_type === 'entity' ? 'Entity' : 'Individual';
  };

  return (
    <div className="container my-4">
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th>ID</th>
              <th>Provider Details</th>
              <th>Type</th>
              <th>Name/Company</th>
              <th>Representative</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Documents</th>
            </tr>
          </thead>
          <tbody>
            {providers.map(provider => (
              <tr key={provider.id}>
                <td>{provider.id}</td>
                <td>
                  <button
                    onClick={() => handleProviderDetails(provider)}
                    className="btn btn-info btn-sm"
                    style={{
                      backgroundColor: '#17a2b8',
                      border: 'none',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#138496'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#17a2b8'}
                  >
                    <i className="bi bi-building"></i>
                    Provider Details
                  </button>
                </td>
                <td>{getProviderType(provider)}</td>
                <td>{getProviderDisplayName(provider)}</td>
                <td>
                  {provider.provider_type === 'entity' ? 
                    `${provider.first_name || ''} ${provider.last_name || ''}`.trim() || 'N/A' :
                    'Same as Name'
                  }
                </td>
                <td>{provider.email || 'N/A'}</td>
                <td>{provider.contact_phone || 'N/A'}</td>
                <td>
                  <span className={`badge ${provider.is_activated ? 'bg-success' : 'bg-warning'}`}>
                    {provider.is_activated ? 'Activated' : 'Pending'}
                  </span>
                </td>
                <td>
                  {provider.documents && provider.documents.length > 0 ? (
                    provider.documents.map(doc => {
                      // Handle URL construction properly - doc.url already starts with /media/
                      const documentUrl = doc.url.startsWith('/') ? 
                        `${BACKEND_URL}${doc.url}` : 
                        `${BACKEND_URL}/${doc.url}`;
                      
                      const fileType = getFileType(doc.url);
                      
                      return (
                        <button
                          key={doc.id}
                          onClick={() => {
                            setLightboxImg(documentUrl);
                            setLightboxDocId(doc.id);
                            setLightboxDocType(fileType);
                          }}
                          style={{ 
                            marginRight: 8, 
                            background: 'none', 
                            border: 'none', 
                            padding: 0,
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          {fileType === 'pdf' ? (
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#e74c3c',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                border: "1px solid #ccc",
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}
                            >
                              PDF
                            </div>
                          ) : fileType === 'document' ? (
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#3498db',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                border: "1px solid #ccc",
                                color: 'white',
                                fontSize: '8px',
                                fontWeight: 'bold'
                              }}
                            >
                              DOC
                            </div>
                          ) : (
                            <img
                              src={documentUrl}
                              alt="doc"
                              style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }}
                              onError={(e) => {
                                // Fallback to DOC icon if image fails to load
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          )}
                          {fileType === 'image' && (
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                backgroundColor: '#3498db',
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                border: "1px solid #ccc",
                                color: 'white',
                                fontSize: '8px',
                                fontWeight: 'bold'
                              }}
                            >
                              IMG
                            </div>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <span className="text-muted">No documents</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Document Lightbox */}
      {lightboxImg && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            flexDirection: 'column'
          }}
          onClick={() => {
            setLightboxImg(null);
            setLightboxDocId(null);
            setLightboxDocType('image');
            setExpiryDate('');
            setComments('');
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '90%',
              maxHeight: '90%',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* Document Display */}
            <div style={{ marginBottom: '20px', maxWidth: '800px', maxHeight: '600px' }}>
              {lightboxDocType === 'pdf' ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <div style={{ fontSize: '72px', color: '#dc3545', marginBottom: '20px' }}>
                    📄
                  </div>
                  <h4 style={{ color: '#333', marginBottom: '20px' }}>PDF Document</h4>
                  <p style={{ color: '#666', marginBottom: '30px' }}>
                    Click the button below to view the PDF document in a new tab.
                  </p>
                  <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a 
                      href={lightboxImg} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#ffffff', 
                        textDecoration: 'none',
                        padding: '12px 24px',
                        backgroundColor: '#007bff',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        transition: 'background-color 0.3s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                    >
                      📖 View PDF
                    </a>
                    <a 
                      href={lightboxImg} 
                      download
                      style={{ 
                        color: '#ffffff', 
                        textDecoration: 'none',
                        padding: '12px 24px',
                        backgroundColor: '#28a745',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        transition: 'background-color 0.3s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#1e7e34'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
                    >
                      💾 Download PDF
                    </a>
                  </div>
                </div>
              ) : lightboxDocType === 'document' ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <div style={{ fontSize: '48px', color: '#3498db', marginBottom: '20px' }}>
                    📄
                  </div>
                  <p>Document preview not available. Click the link below to download:</p>
                  <a 
                    href={lightboxImg} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#007bff', textDecoration: 'underline' }}
                  >
                    Download Document
                  </a>
                </div>
              ) : (
                <img 
                  src={lightboxImg} 
                  alt="Document" 
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              )}
            </div>

            {/* Action Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%', maxWidth: '400px' }}>
              {/* Expiry Date Input */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Expiry Date (required for acceptance):
                </label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: '1px solid #ccc' 
                  }}
                />
              </div>

              {/* Comments Input */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Comments (required for rejection):
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Enter comments for rejection..."
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: '1px solid #ccc',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={handleAccept}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Accept
                </button>
                <button
                  onClick={handleReject}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Reject
                </button>
                <button
                  onClick={() => {
                    setLightboxImg(null);
                    setLightboxDocId(null);
                    setLightboxDocType('image');
                    setExpiryDate('');
                    setComments('');
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default ProviderDocsTable;